import { supabase } from "@/platform/lib/supabase";
import { readErrorFromResponse } from "@/platform/billing/errors";

export interface EssayPromptRow {
  id: string;
  year: number;
  theme: string;
  command: string;
  collection_texts: Array<{ label: string; body: string }> | null;
}

export interface GradeEssayPayload {
  prompt_id?: string;
  theme: string;
  command: string;
  collection_excerpt?: string;
  body?: string;
  image_paths?: string[];
}

export interface GradeEssayResult {
  transcription?: string;
  scores: {
    tema: number;
    estrutura: number;
    lingua: number;
    coesao: number;
    total: number;
  };
  feedback: string;
  rewrite_plan: string;
  model_risk: string[];
  confidence: number;
  submission_id: string | null;
  source: "text" | "image";
}

export async function listEssayPrompts(): Promise<EssayPromptRow[]> {
  const { data, error } = await supabase
    .from("einstein_essay_prompts")
    .select("id, year, theme, command, collection_texts")
    .order("year", { ascending: false });
  if (error) throw error;
  return (data as EssayPromptRow[]) ?? [];
}

/**
 * Upload de imagens manuscritas para einstein-essays bucket.
 * Path: <user_id>/<submission_uuid>/page-N.<ext>
 * Retorna os paths gravados.
 */
export async function uploadEssayImages(params: {
  userId: string;
  submissionUuid: string;
  files: File[];
}): Promise<string[]> {
  const paths: string[] = [];
  for (let i = 0; i < params.files.length; i++) {
    const file = params.files[i];
    if (!file) continue;
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${params.userId}/${params.submissionUuid}/page-${i + 1}.${ext}`;
    const { error } = await supabase.storage
      .from("einstein-essays")
      .upload(path, file, { contentType: file.type, upsert: true });
    if (error) throw new Error(`upload ${path}: ${error.message}`);
    paths.push(path);
  }
  return paths;
}

export async function gradeEssay(payload: GradeEssayPayload): Promise<GradeEssayResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("Sessão expirada. Entre novamente.");

  const res = await fetch("/api/grade-essay", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw await readErrorFromResponse(res);
  }
  return (await res.json()) as GradeEssayResult;
}
